import { Button, SimpleGrid, Stack } from "@mantine/core";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { IconBuildingChurch, IconCircleCheck, IconLifebuoy, IconPlus } from "@tabler/icons-react";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router";
import {
  DataTable,
  FilterBar,
  LiveIndicator,
  PageHeader,
  StatCard,
  StatusBadge,
  useSearchParamState,
} from "@how/ui";
import { isAuthorizationError } from "../../lib/query-client";
import { usePlatformRole } from "../auth/platform-role";
import { PlanBadge } from "../plans/PlanBadge";
import { usePlatformStats, useTenants, type TenantRow, type TenantSort } from "./queries";

const PAGE_SIZE = 25;
const SORTABLE = new Set<TenantSort["id"]>(["name", "slug", "status", "created_at"]);

const columns: ColumnDef<TenantRow>[] = [
  { accessorKey: "name", header: "Name" },
  { accessorKey: "slug", header: "Slug" },
  {
    accessorKey: "status",
    header: "Status",
    cell: (ctx) => <StatusBadge domain="tenant_status" value={ctx.row.original.status} />,
  },
  {
    accessorKey: "plan",
    header: "Plan",
    enableSorting: false,
    cell: (ctx) => <PlanBadge plan={ctx.row.original.plan} />,
  },
  {
    accessorKey: "created_at",
    header: "Created",
    // Dates are alignment-sensitive: right-aligned with tabular figures so the column reads as a column.
    meta: { numeric: true },
    cell: (ctx) => new Date(ctx.row.original.created_at).toLocaleDateString(),
  },
];

/**
 * Tenant list: search, sort, and page state live in the URL; data comes paginated from the server.
 *
 * This is the reference screen for the surface, density, and semantic rules in docs/UI_STANDARDS.md: metric panels
 * over a framed table, quiet borders instead of shadows, one primary action, and status and live state carried by
 * words as well as color.
 */
export function TenantsPage() {
  const navigate = useNavigate();
  const role = usePlatformRole();
  const [search, setSearch] = useSearchParamState("q");
  const [page, setPage] = useSearchParamState("page", "1");
  const [sortParam, setSortParam] = useSearchParamState("sort", "name.asc");
  const [sortId, sortDir] = sortParam.split(".");
  const sort: TenantSort = {
    id: SORTABLE.has(sortId as TenantSort["id"]) ? (sortId as TenantSort["id"]) : "name",
    desc: sortDir === "desc",
  };
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);
  const tenants = useTenants({ search, pageIndex, pageSize: PAGE_SIZE, sort });
  /** Shared query key with the overview, so this adds no second round trip when both are visited. */
  const stats = usePlatformStats();
  const sorting = useMemo<SortingState>(
    () => [{ id: sort.id, desc: sort.desc }],
    [sort.id, sort.desc],
  );
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );

  const status = tenants.isPending
    ? "loading"
    : tenants.isError
      ? isAuthorizationError(tenants.error)
        ? "denied"
        : "error"
      : "ready";
  const total = stats.data?.tenants ?? 0;
  const active = stats.data?.activeTenants ?? 0;
  const inactive = Math.max(0, total - active);
  const sessions = stats.data?.activeSessions ?? 0;

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Every church and worship organization on the platform."
        actions={
          // A super admin's action (ADR 0064): DCX support has no button, and the database refuses them anyway.
          role.data === "super_admin" ? (
            <Button component={Link} to="/tenants/new" leftSection={<IconPlus size={16} />}>
              Create church
            </Button>
          ) : null
        }
      />
      <Stack gap="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <StatCard
            emphasis="hero"
            label="Churches on the platform"
            value={total.toLocaleString()}
            loading={stats.isPending}
            icon={<IconBuildingChurch size={20} />}
            hint="Every tenant, whatever its status."
          />
          <StatCard
            label="Active"
            value={active.toLocaleString()}
            loading={stats.isPending}
            icon={<IconCircleCheck size={18} />}
            role="success"
            hint={
              inactive === 0
                ? "Every church is active."
                : `${String(inactive)} not active: provisioning, suspended, or offboarding.`
            }
          />
          <StatCard
            label="Support access in use"
            value={sessions.toLocaleString()}
            loading={stats.isPending}
            icon={<IconLifebuoy size={18} />}
            role={sessions > 0 ? "warning" : "neutral"}
            trailing={
              <LiveIndicator live={sessions > 0} label="Sessions open" idleLabel="None open" />
            }
            hint="DCX Logic staff working inside a church's data."
          />
        </SimpleGrid>
        <div>
          <FilterBar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(undefined);
            }}
            searchPlaceholder="Search tenants"
          />
          <DataTable
            label="Tenants"
            columns={columns}
            rows={tenants.data?.rows ?? []}
            totalRows={tenants.data?.total ?? 0}
            status={status}
            pagination={pagination}
            onPaginationChange={(updater) => {
              const next = typeof updater === "function" ? updater(pagination) : updater;
              setPage(String(next.pageIndex + 1));
            }}
            sorting={sorting}
            onSortingChange={(updater) => {
              const next = typeof updater === "function" ? updater(sorting) : updater;
              const first = next[0];
              setSortParam(
                first === undefined ? undefined : `${first.id}.${first.desc ? "desc" : "asc"}`,
              );
            }}
            getRowId={(row) => row.id}
            onRowClick={(row) => {
              void navigate(`/tenants/${row.id}`);
            }}
            emptyTitle="No tenants match"
            emptyDescription="Try a different search."
            errorMessage="Could not load tenants."
            onRetry={() => {
              void tenants.refetch();
            }}
          />
        </div>
      </Stack>
    </>
  );
}
